import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Pages/Services.css';

const Services = () => {
  const [activeService, setActiveService] = useState(null);

  useEffect(() => {
    document.title = 'IT Services & Solutions - Pentagram Infotech';
    document.querySelector('meta[name="description"]')?.setAttribute('content',
      'Explore our comprehensive IT services including cloud computing, cybersecurity, IT support, software solutions, and placements. Transform your business with Pentagram Infotech.'
    );

    // Add structured data for services
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "IT Services",
      "provider": {
        "@type": "Organization",
        "name": "Pentagram Infotech Private Limited",
        "url": "https://pentagraminfotech.com"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "IT Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "IT Services",
              "description": "Comprehensive IT infrastructure management and support"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Cloud Computing",
              "description": "Scalable cloud solutions including migration and management"
            }
          }
        ]
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      id: 1,
      title: 'IT Services',
      icon: 'bi-laptop',
      color: '#1a56db',
      shortDescription: 'Comprehensive IT infrastructure management and support services',
      fullDescription: 'We provide end-to-end IT services including network management, system administration, IT consulting, and infrastructure support. Our team ensures your IT infrastructure runs smoothly and efficiently.',
      features: [
        'Network Design & Implementation',
        'System Administration & Maintenance',
        'IT Consulting & Strategy',
        'Infrastructure Management',
        'Disaster Recovery Planning',
        'IT Audits & Assessments'
      ],
      benefits: [
        'Improved system reliability',
        'Reduced downtime',
        'Enhanced security',
        'Cost-effective solutions',
        'Scalable infrastructure'
      ],
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80'
    },
    {
      id: 2,
      title: 'Cloud Computing',
      icon: 'bi-cloud',
      color: '#3b82f6',
      shortDescription: 'Scalable cloud solutions including migration, management, and optimization',
      fullDescription: 'Our cloud computing services help businesses leverage the power of cloud technology. We offer cloud migration, management, optimization, and DevOps services across multiple platforms.',
      features: [
        'Cloud Migration Services',
        'Multi-cloud Management',
        'DevOps & Automation',
        'Cloud Security',
        'Cost Optimization',
        '24/7 Cloud Support'
      ],
      benefits: [
        'Scalability & Flexibility',
        'Cost Savings',
        'Enhanced Collaboration',
        'Business Continuity',
        'Automatic Updates'
      ],
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1352&q=80'
    },
    {
      id: 3,
      title: 'Cyber Security',
      icon: 'bi-shield-check',
      color: '#0e9f6e',
      shortDescription: 'Advanced security solutions to protect your digital assets',
      fullDescription: 'Protect your business from cyber threats with our comprehensive cybersecurity solutions. We offer threat protection, risk assessment, compliance management, and security monitoring.',
      features: [
        'Threat Detection & Prevention',
        'Vulnerability Assessment',
        'Security Compliance',
        'Data Encryption',
        'Incident Response',
        'Security Awareness Training'
      ],
      benefits: [
        'Enhanced data protection',
        'Regulatory compliance',
        'Reduced security risks',
        'Business reputation protection',
        'Peace of mind'
      ],
      image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80'
    },
    {
      id: 4,
      title: 'IT Support',
      icon: 'bi-headset',
      color: '#8b5cf6',
      shortDescription: '24/7 technical support and helpdesk services',
      fullDescription: 'Our IT support services ensure uninterrupted business operations with 24/7 technical assistance, remote support, on-site support, and helpdesk solutions.',
      features: [
        '24/7 Help Desk Support',
        'Remote Technical Support',
        'On-site IT Support',
        'Hardware & Software Support',
        'User Training & Support',
        'IT Asset Management'
      ],
      benefits: [
        'Quick issue resolution',
        'Reduced downtime',
        'Expert technical assistance',
        'Proactive maintenance',
        'Cost-effective support'
      ],
      image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80'
    },
    {
      id: 5,
      title: 'IT Products',
      icon: 'bi-hdd-stack',
      color: '#f59e0b',
      shortDescription: 'High-quality hardware and software solutions',
      fullDescription: 'We provide a wide range of IT products including hardware, software, networking equipment, and peripherals from leading manufacturers at competitive prices.',
      features: [
        'Hardware Procurement',
        'Software Licensing',
        'Network Equipment',
        'Peripheral Devices',
        'IT Asset Procurement',
        'Product Lifecycle Management'
      ],
      benefits: [
        'Quality products',
        'Competitive pricing',
        'Warranty support',
        'Technical guidance',
        'Single point of contact'
      ],
      image: 'https://images.unsplash.com/photo-1531297484001-80022131f5a1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80'
    },
    {
      id: 6,
      title: 'Software Solutions',
      icon: 'bi-code-square',
      color: '#ef4444',
      shortDescription: 'Custom software development and enterprise applications',
      fullDescription: 'We develop custom software solutions tailored to your business needs, including web applications, mobile apps, enterprise software, and integration services.',
      features: [
        'Custom Software Development',
        'Web Application Development',
        'Mobile App Development',
        'Enterprise Software Solutions',
        'API Development & Integration',
        'Software Testing & QA'
      ],
      benefits: [
        'Tailored solutions',
        'Competitive advantage',
        'Process automation',
        'Improved efficiency',
        'Scalable solutions'
      ],
      image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80'
    },
    {
      id: 7,
      title: 'Placements',
      icon: 'bi-people',
      color: '#ec4899',
      shortDescription: 'IT staffing solutions and talent acquisition',
      fullDescription: 'We provide IT staffing solutions including permanent placements, contract staffing, staff augmentation, and talent acquisition services for IT roles.',
      features: [
        'IT Recruitment Services',
        'Contract Staffing',
        'Permanent Placements',
        'Staff Augmentation',
        'Talent Acquisition',
        'Skill Assessment'
      ],
      benefits: [
        'Access to skilled talent',
        'Reduced hiring time',
        'Quality candidates',
        'Flexible staffing options',
        'Industry expertise'
      ],
      image: 'https://images.unsplash.com/photo-1551836026-d5c2d0ba4a29?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80'
    }
  ];

  const handleServiceClick = (serviceId) => {
    if (activeService === serviceId) {
      setActiveService(null);
    } else {
      setActiveService(serviceId);
    }
  };

  return (
    <div className="services-page page-transition">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="container">
          <div className="row align-items-center min-vh-60">
            <div className="col-lg-8 mx-auto text-center">
              <h1 className="page-title">Our IT Services</h1>
              <p className="page-subtitle">
                Comprehensive IT solutions designed to transform your business and drive digital innovation.
                We provide end-to-end services to meet all your technology needs.
              </p>
              <div className="service-stats">
                <div className="stat">
                  <span className="number">7</span>
                  <span className="label">Core Services</span>
                </div>
                <div className="stat">
                  <span className="number">25+</span>
                  <span className="label">Years Experience</span>
                </div>
                <div className="stat">
                  <span className="number">500+</span>
                  <span className="label">Projects Delivered</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Navigation */}
      <section className="services-navigation">
        <div className="container">
          <div className="service-tabs">
            {services.map((service) => (
              <button
                key={service.id}
                className={`service-tab ${activeService === service.id ? 'active' : ''}`}
                onClick={() => handleServiceClick(service.id)}
                style={{ '--service-color': service.color }}
              >
                <i className={`bi ${service.icon}`}></i>
                <span>{service.title}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Details */}
      <section className="services-details section-padding">
        <div className="container">
          {services.map((service) => (
            <div
              key={service.id}
              className={`service-detail ${activeService === service.id ? 'active' : ''}`}
              id={`service-${service.id}`}
            >
              <div className="row align-items-center">
                <div className="col-lg-6">
                  <div className="service-content">
                    <div className="service-header">
                      <div 
                        className="service-icon-large"
                        style={{ background: service.color }}
                      >
                        <i className={`bi ${service.icon}`}></i>
                      </div>
                      <h2 className="service-title">{service.title}</h2>
                      <p className="service-description">{service.fullDescription}</p>
                    </div>

                    <div className="service-info">
                      <div className="features-section">
                        <h4>
                          <i className="bi bi-check-circle"></i>
                          Key Features
                        </h4>
                        <ul className="features-list">
                          {service.features.map((feature, idx) => (
                            <li key={idx}>
                              <i className="bi bi-check"></i>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="benefits-section">
                        <h4>
                          <i className="bi bi-star"></i>
                          Business Benefits
                        </h4>
                        <ul className="benefits-list">
                          {service.benefits.map((benefit, idx) => (
                            <li key={idx}>
                              <i className="bi bi-arrow-right"></i>
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="service-cta">
                      <Link to="/contact" className="btn btn-primary" style={{ background: service.color }}>
                        Get {service.title} Service
                        <i className="bi bi-arrow-right ms-2"></i>
                      </Link>
                      <Link to="/contact" className="btn btn-outline-primary ms-3">
                        Request Consultation
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="service-image">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="img-fluid rounded"
                    />
                    <div className="image-overlay">
                      <div className="overlay-content">
                        <h5>Ready to Transform?</h5>
                        <p>Contact us for a free consultation about our {service.title.toLowerCase()} services.</p>
                        <Link to="/contact" className="btn btn-light">
                          Contact Now
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section section-padding bg-light">
        <div className="container">
          <div className="section-header text-center mb-5">
            <span className="section-label">Our Process</span>
            <h2 className="section-title">How We Work</h2>
            <p className="section-subtitle">
              Our proven methodology ensures successful delivery of IT solutions
            </p>
          </div>

          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">01</div>
              <div className="step-content">
                <h4>Discovery & Analysis</h4>
                <p>We analyze your business needs and technical requirements to understand your goals.</p>
              </div>
            </div>
            
            <div className="process-step">
              <div className="step-number">02</div>
              <div className="step-content">
                <h4>Planning & Strategy</h4>
                <p>We develop a comprehensive strategy and roadmap for implementation.</p>
              </div>
            </div>
            
            <div className="process-step">
              <div className="step-number">03</div>
              <div className="step-content">
                <h4>Implementation</h4>
                <p>Our experts implement the solution with attention to quality and detail.</p>
              </div>
            </div>
            
            <div className="process-step">
              <div className="step-number">04</div>
              <div className="step-content">
                <h4>Testing & Quality</h4>
                <p>Rigorous testing ensures the solution meets all requirements and standards.</p>
              </div>
            </div>
            
            <div className="process-step">
              <div className="step-number">05</div>
              <div className="step-content">
                <h4>Deployment</h4>
                <p>Seamless deployment with minimal disruption to your operations.</p>
              </div>
            </div>
            
            <div className="process-step">
              <div className="step-number">06</div>
              <div className="step-content">
                <h4>Support & Maintenance</h4>
                <p>Ongoing support and maintenance to ensure optimal performance.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta">
        <div className="container">
          <div className="cta-content text-center">
            <h2>Ready to Transform Your Business?</h2>
            <p>Get in touch with our experts to discuss your IT needs and discover how we can help.</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary btn-lg">
                Contact Us Today
                <i className="bi bi-arrow-right ms-2"></i>
              </Link>
              <Link to="/about" className="btn btn-outline-light btn-lg ms-3">
                Learn About Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;